import { PRODUCT_NAMES } from '../constants';
const {
  LEARN,
  PERFORM,
  RECRUIT,
  HR
} = PRODUCT_NAMES;
/**
 * Array of products that are used to determine whether the Suite product is shown.
 * If the suite user is not a user in one of these products then the Suite product is hidden.
 * HR is excluded from this array because it does not (yet) have any data to show.
 */

const productsToShowSuite = [LEARN, PERFORM, RECRUIT];
const REPORTING_ROLE_PREFIX = 'Reporting.';
/**
 * Returns true if the suite user is a reporting administrator.
 * @param {string} iss
 * @param {string | string[]} role - The role(s) for the suite user.
 * @returns {boolean} - True if the suite user is a reporting administrator.
 */

const isUserAnyReportingUser = ({
  claims: {
    iss,
    role
  } = {}
} = {}) => {
  if (typeof iss !== 'string' || !iss.endsWith('/identity')) {
    return false;
  }

  return typeof role === 'string' && role.startsWith(REPORTING_ROLE_PREFIX) || Array.isArray(role) && role.some(roleValue => roleValue.startsWith(REPORTING_ROLE_PREFIX));
};
/**
 * Returns true if the suite user is an identity administrator.
 * @param {string | string[]} role - The role(s) for the suite user.
 * @returns {boolean} - True if the suite user is an identity administrator.
 */


const isIdentityAdmin = ({
  claims: {
    role
  } = {}
} = {}) => typeof role === 'string' && role === 'kipAdmin' || Array.isArray(role) && role.includes('kipAdmin');
/**
 * Returns true if the suite user is a user in an accessible product.
 * @param {SuiteUser?} suiteUser - The suite user.
 * @param {string?} accessibleProductName - The accessible product name.
 * @returns {boolean} - True if the suite user is a user in the accessible product.
 */


const isUserInAccessibleProduct = (suiteUser = {}, accessibleProductName = undefined) => {
  const productUser = {
    [LEARN]: ({
      rawUsers: {
        learnUser
      } = {}
    } = {}) => learnUser,
    [PERFORM]: ({
      rawUsers: {
        performUser
      } = {}
    } = {}) => performUser,
    [RECRUIT]: ({
      rawUsers: {
        recruitUser
      } = {}
    } = {}) => recruitUser,
    [HR]: ({
      rawUsers: {
        hrUser
      } = {}
    } = {}) => hrUser
  }[accessibleProductName]?.(suiteUser);
  return typeof productUser === 'object' && Object.keys(productUser).length > 0;
};
/**
 * Returns true if the suite user is a manager in an accessible product and is a user in that product.
 * @param {SuiteUser?} suiteUser - The suite user.
 * @param {string[]?} accessibleProductNames - The accessible product names.
 * @returns {boolean} - True if the suite user is a manager in an accessible product.
 */


const isManagerInAccessibleProduct = (suiteUser = {}, accessibleProductNames = []) => {
  const predicates = {
    [LEARN]: ({
      productManager: {
        learn
      } = {}
    } = {}) => learn === true,
    [PERFORM]: ({
      productManager: {
        perform
      } = {}
    } = {}) => perform === true,
    [RECRUIT]: ({
      productManager: {
        recruit
      } = {}
    } = {}) => recruit === true,
    [HR]: ({
      productManager: {
        hr
      } = {}
    } = {}) => hr === true
  };
  return accessibleProductNames.some(productName => predicates[productName]?.(suiteUser) && isUserInAccessibleProduct(suiteUser, productName));
};
/**
 * Returns true if the accessible product names include at least one of Learn, Perform, Recruit and the suite user is a user in that product.
 * @param {SuiteUser?} suiteUser - The suite user.
 * @param {string[]?} accessibleProductNames - The accessible product names.
 * @returns {boolean} - True if the Suite product is accessible.
 */


const isSuiteProductAccessible = (suiteUser = {}, accessibleProductNames = []) => accessibleProductNames.some(productName => productsToShowSuite.includes(productName) && isUserInAccessibleProduct(suiteUser, productName));
/**
 * Returns an array of <em>user</em> accessible tab names based on the suite user and the accessible tab names.
 * @example
 * // returns [
 * //   PRODUCT_NAMES.LEARN,
 * //   PRODUCT_NAMES.IDENTITY_ADMIN,
 * // ]
 * const userAccessibleProductNames = getUserAccessibleProductNames(
 *   {
 *     claims: {
 *       role: 'kipAdmin',
 *     },
 *     rawUsers: {
 *       learnUser: {
 *         id: '7ec84f04-76ec-41a5-ad23-40cfcee5a1f9',
 *       },
 *     },
 *   },
 *   [
 *     PRODUCT_NAMES.LEARN,
 *     PRODUCT_NAMES.PERFORM,
 *     PRODUCT_NAMES.IDENTITY_ADMIN,
 *   ],
 * );
 * @param {SuiteUser?} suiteUser - The suite user.
 * @param {string[]?} accessibleProductNames - The accessible tab names.
 * @returns {string[]} - An array of <em>user</em> accessible tab names.
 * @see {ProductTab#name}
 */


const getUserAccessibleProductNames = (suiteUser = {}, accessibleProductNames = []) => {
  if (!suiteUser.rawUsers || accessibleProductNames.length === 0) return [];
  const productsNamesWhereUserHasAccount = accessibleProductNames.filter(productName => isUserInAccessibleProduct(suiteUser, productName));

  if (isSuiteProductAccessible(suiteUser, accessibleProductNames) && accessibleProductNames.includes(PRODUCT_NAMES.SUITE)) {
    productsNamesWhereUserHasAccount.push(PRODUCT_NAMES.SUITE);

    if (isManagerInAccessibleProduct(suiteUser, accessibleProductNames) && accessibleProductNames.includes(PRODUCT_NAMES.TEAM)) {
      productsNamesWhereUserHasAccount.push(PRODUCT_NAMES.TEAM);
    }
  }

  if (isIdentityAdmin(suiteUser) && accessibleProductNames.includes(PRODUCT_NAMES.IDENTITY_ADMIN)) {
    productsNamesWhereUserHasAccount.push(PRODUCT_NAMES.IDENTITY_ADMIN);
  }

  if (isUserAnyReportingUser(suiteUser) && accessibleProductNames.includes(PRODUCT_NAMES.REPORTING)) {
    productsNamesWhereUserHasAccount.push(PRODUCT_NAMES.REPORTING);
  }

  return productsNamesWhereUserHasAccount;
};

export default getUserAccessibleProductNames;
//# sourceMappingURL=getUserAccessibleProductNames.js.map